import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { changeRequestsAPI, projectsAPI } from '../utils/api';
import { Badge, ConfirmDialog, Modal, FormField, SelectField } from '../components/layout/UI';
import { format, parseISO, isValid } from 'date-fns';
import { useAuth } from '../hooks/useAuth';
import { isAdminRole } from '../utils/rolesConfig';
import { useToast, apiError } from '../components/Toast';
import { useMasters } from '../hooks/useMasters';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import { ArrowLeft, Calendar, SquarePen, Trash2 } from 'lucide-react';
// Permission gate applied to approve/reject/implement action buttons below

const EditIc = () => <SquarePen size={14} />;
const TrashIc = () => <Trash2 size={14} />;
const CalIc = () => <Calendar size={13} />;

const ENV_STYLE   = { Production:'border border-red-500 text-red-600 dark:text-red-400', UAT:'border border-amber-500 text-amber-600 dark:text-amber-400', Development:'border border-blue-500 text-blue-600 dark:text-blue-400' };
// Lifecycle order for the progress stepper. Rejected is handled separately
// as an off-ramp, so it is deliberately not in this list.
const STATUS_FLOW = ['Pending','Under Review','Requirements Shared','BRD Signed Off',
                     'Approved','In Development','Testing','Deployed','Implemented'];
const STATUS_COLORS = { Pending:'#64748b', 'Under Review':'#8b5cf6', Approved:'#10b981', Rejected:'#ef4444', Implemented:'#3b82f6' };
/* Fallbacks only. The live lists come from Masters inside the component,
 * the same categories ChangeRequestFormFields already uses, so this modal
 * and the create form cannot offer different options. */
const APPROVAL_FALLBACK = ['Pending','Under Review','Requirements Shared','BRD Signed Off',
                       'Approved','In Development','Testing','Deployed',
                       'Implemented','Rejected'].map(v=>({value:v,label:v}));
const IMPACT_FALLBACK = ['Low','Medium','High','Critical'].map(v=>({value:v,label:v}));
const ENV_FALLBACK    = ['Development','UAT','Production'].map(v=>({value:v,label:v}));

const fmt = (val, pattern = 'MMM d, yyyy') => {
  if (!val) return null;
  try { const d = typeof val === 'string' ? parseISO(val) : new Date(val); return isValid(d) ? format(d, pattern) : null; }
  catch { return null; }
};
const InfoRow = ({ label, value, children }) => (
  value || children ? (
    <div className="flex gap-3 py-2.5 border-b" style={{ borderColor:'var(--border)' }}>
      <p className="text-xs font-semibold w-32 flex-shrink-0 pt-0.5 uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{label}</p>
      <div className="flex-1 text-sm" style={{ color:'var(--text)' }}>{children || value}</div>
    </div>
  ) : null
);

export default function CRDetailPage() {
  const toast = useToast();
  // bug_environment is shared with Change Requests on purpose: it is the
  // category ChangeRequestFormFields already reads, and the two must agree.
  const m = useMasters({
    approval:    ['cr_status',       APPROVAL_FALLBACK],
    impact:      ['cr_impact',       IMPACT_FALLBACK],
    environment: ['bug_environment', ENV_FALLBACK],
  });
  const { id } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [cr,         setCR]         = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [projects,   setProjects]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen,   setEditOpen]   = useState(false);
  const [editForm,   setEditForm]   = useState({});
  const [saving,     setSaving]     = useState(false);
  // Must stay up here with the other hooks. Below the `if (loading) return`
  // guard it only runs on the second render, and React counts a different
  // number of hooks between renders: "Rendered more hooks than during the
  // previous render."
  const [submitting, setSubmitting] = useState(false);

  const f = v => setEditForm(prev => ({ ...prev, ...v }));

  // Named, so the actions below can refresh after they change something.
  // `load()` was being called by the submit-for-review handler and did not exist.
  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([changeRequestsAPI.getById(id), projectsAPI.getAll({ limit: 0 })])
      .then(([cR, pR]) => { setCR(cR.data); setProjects(pR.data); })
      .catch(e => toast.error(apiError(e, 'Could not load this page.')))
      .finally(() => setLoading(false));
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setEditForm({ project_id:cr.project_id, title:cr.title, description:cr.description||'', business_justification:cr.business_justification||'', impact_level:cr.impact_level, approval_status:cr.approval_status, implementation_date:cr.implementation_date?.split('T')[0]||'', environment:cr.environment||'Development',
      // Without this the custom-field editor opens empty even when values
      // are stored. The API merges on save, so nothing was ever lost — but
      // the person could not see what was already there.
      custom_fields: cr.custom_fields || {} });
    setEditOpen(true);
  };
  const handleSave = async () => {
    setSaving(true);
    try { await changeRequestsAPI.update(id, editForm);
    toast.success('Change request updated'); const r = await changeRequestsAPI.getById(id); setCR(r.data); setEditOpen(false); }
    catch(e) { toast.error(apiError(e, 'Failed to save change request.')); } finally { setSaving(false); }
  };
  const handleDelete = async () => {
    try { await changeRequestsAPI.delete(id); toast.success('Change request deleted'); navigate('/change-requests'); }
    catch(e) { toast.error(apiError(e, 'Could not delete that change request.')); }
  };


  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!cr) return <div className="card text-center py-16"><p style={{ color:'var(--text)' }}>Change request not found</p><button onClick={() => navigate('/change-requests')} className="btn-secondary mt-3">← Back</button></div>;


  /** One step out of Pending. Shown only when it is actually available. */
  const canSubmit = cr.approval_status === 'Pending' &&
                    (cr.requested_by === me?.id || isAdminRole(me?.role));
  const handleSubmitForReview = async () => {
    setSubmitting(true);
    try {
      await changeRequestsAPI.submitForReview(id);
      toast.success('Submitted for review — the assignee has been notified');
      load();
    } catch(e) {
      toast.error(apiError(e, 'Could not submit this for review.'));
    } finally { setSubmitting(false); }
  };

  const currentIdx = STATUS_FLOW.indexOf(cr.approval_status);
  const isRejected = cr.approval_status === 'Rejected';

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-sm flex-wrap" style={{ color:'var(--text-secondary)' }}>
        <button onClick={() => navigate('/change-requests')} className="hover:text-brand-500 flex items-center gap-1"><ArrowLeft size={16}/> Change Requests</button>
        {cr.project_name && <><span>/</span><span>{cr.project_name}</span></>}
        <span>/</span><span style={{ color:'var(--text)' }}>{cr.title}</span>
      </div>

      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div className="flex-1">
            <h1 className="text-2xl font-bold mb-2" style={{ color:'var(--text)' }}>{cr.title}</h1>
            <div className="flex flex-wrap gap-2">
              <Badge value={cr.approval_status} type="status"   />
              <Badge value={cr.impact_level}    type="priority" />
              {cr.environment && <span className={`badge ${ENV_STYLE[cr.environment]||''}`}>{cr.environment}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={openEdit} className="btn-secondary flex items-center gap-1.5 text-sm"><EditIc /> Edit</button>
            {(cr?.requested_by === me?.id || isAdminRole(me?.role)) && (
              <button onClick={() => setDeleteOpen(true)} className="btn-danger flex items-center gap-1.5 text-sm"><TrashIc /> Delete</button>
            )}
          </div>
        </div>

        {canSubmit && (
          <div className="card flex items-center justify-between gap-4 py-3 px-4 mb-4">
            <p className="text-sm" style={{ color:'var(--text-secondary)' }}>
              This change request is still <strong style={{ color:'var(--text)' }}>Pending</strong>.
              Submitting it moves it to Under Review and notifies the assignee.
            </p>
            <button onClick={handleSubmitForReview} disabled={submitting}
              className="btn-primary text-sm flex items-center gap-2 flex-shrink-0">
              {submitting && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              Submit for review
            </button>
          </div>
        )}

        {/* Approval stepper */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color:'var(--text-secondary)' }}>Approval Progress</p>
          <div className="flex items-center">
            {STATUS_FLOW.map((step, i) => (
              <div key={step} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2"
                    style={{
                      background: !isRejected && (currentIdx > i || cr.approval_status === step) ? STATUS_COLORS[step] : 'var(--surface-alt)',
                      borderColor: !isRejected && (currentIdx > i || cr.approval_status === step) ? STATUS_COLORS[step] : 'var(--border)',
                      color: !isRejected && (currentIdx > i || cr.approval_status === step) ? 'white' : 'var(--text-secondary)'
                    }}>
                    {!isRejected && currentIdx > i ? '✓' : i + 1}
                  </div>
                  <p className="text-xs mt-1 whitespace-nowrap" style={{ color: cr.approval_status===step ? STATUS_COLORS[step] : 'var(--text-secondary)', fontWeight: cr.approval_status===step ? 600 : 400, fontSize:10 }}>{step}</p>
                </div>
                {i < STATUS_FLOW.length - 1 && (
                  <div className="flex-1 h-0.5 mx-1 mb-5" style={{ background: !isRejected && currentIdx > i ? STATUS_COLORS[STATUS_FLOW[i]] : 'var(--border)' }} />
                )}
              </div>
            ))}
          </div>
          {isRejected && (
            <div className="mt-3 p-3 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400">
              ✗ This change request has been <strong>Rejected</strong>
            </div>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b flex gap-1 overflow-x-auto" style={{ borderColor:'var(--border)' }}>
        {[
          { key:'overview',  label:'Overview'    },
          { key:'timeline',  label:'BRD Timeline'},
          { key:'hours',     label:'Estimation'  },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors
              ${activeTab === tab.key ? 'border-brand-500 text-brand-500' : 'border-transparent'}`}
            style={activeTab !== tab.key ? { color:'var(--text-secondary)' } : {}}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card">
          <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Request Info</h3>
          <InfoRow label="Project">{cr.project_name ? <Link to={`/projects/${cr.project_id}`} className="text-brand-500 hover:underline">{cr.project_name}</Link> : '—'}</InfoRow>
          <InfoRow label="Requested By" value={cr.requested_by_name} />
          <InfoRow label="Approved By"  value={cr.approved_by_name} />
          <InfoRow label="Submitted"    value={fmt(cr.created_at)} />
          <InfoRow label="Updated"      value={fmt(cr.updated_at)} />
          {cr.implementation_date && <InfoRow label="Target Date"><span className="flex items-center gap-1"><CalIc />{fmt(cr.implementation_date)}</span></InfoRow>}
        </div>

        <div className="lg:col-span-2 space-y-5">
          {cr.description && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Description</h3>
              <p className="text-sm leading-relaxed" style={{ color:'var(--text)' }}>{cr.description}</p>
            </div>
          )}
          {cr.business_justification && (
            <div className="card" style={{ background:'var(--surface-alt)' }}>
              <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Business Justification</h3>
              <p className="text-sm leading-relaxed" style={{ color:'var(--text)' }}>{cr.business_justification}</p>
            </div>
          )}
        </div>
      </div>}

      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title="Edit Change Request" size="lg">
        <div className="space-y-4">
          <FormField label="Project" required>
            <select className="select" value={editForm.project_id||''} onChange={e=>f({project_id:e.target.value})}>
              {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FormField>
          <FormField label="Title" required><input className="input" value={editForm.title||''} onChange={e=>f({title:e.target.value})} /></FormField>
          <FormField label="Description"><textarea className="input" rows={3} value={editForm.description||''} onChange={e=>f({description:e.target.value})} /></FormField>
          <FormField label="Business Justification"><textarea className="input" rows={2} value={editForm.business_justification||''} onChange={e=>f({business_justification:e.target.value})} /></FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Impact Level"><SelectField value={editForm.impact_level||''} onChange={e=>f({impact_level:e.target.value})} options={m.impact} placeholder="" /></FormField>
            <FormField label="Approval Status"><SelectField value={editForm.approval_status||''} onChange={e=>f({approval_status:e.target.value})} options={m.approval} placeholder="" /></FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Environment"><SelectField value={editForm.environment||''} onChange={e=>f({environment:e.target.value})} options={m.environment} placeholder="" /></FormField>
            <FormField label="Implementation Date"><input type="date" className="input" value={editForm.implementation_date||''} onChange={e=>f({implementation_date:e.target.value})} /></FormField>
          </div>
          {/* Fields defined under Admin, Custom Fields for Change Requests. */}
          <CustomFieldsEditor appliesTo="change_request"
            values={editForm.custom_fields || {}}
            onChange={v => f({ custom_fields: v })} />
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setEditOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving||!editForm.title} className="btn-primary flex items-center gap-2">
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              Save Changes
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={handleDelete}
        title="Delete Change Request" message={`Delete "${cr.title}" permanently?`} />
    </div>
  );
}
